import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Descendant } from 'slate';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { initializeTemplateWithMetadata } from '../../utils/initializeTemplateWithMetadata';
import { getOnePagerTemplateData, saveOnePagerTemplateData } from '../../store/investor/investorApiHelper';
import { fetchCompanyOverviewData } from '../../store/dashboard/overview/actions';
import { OverviewData } from '../../store/dashboard/overview/types';
import { DATE_FORMAT } from '../../utils/constants/commonConstants';
import { Mixpanel } from '../../utils/mixpanel';
import { getSubdomain } from '../../utils/utils';

import { Button, Modal } from '../_custom';
import DocumentEditor from './DocumentEditor';
import { sanitizeSlateValue } from '../TextEditor/utils/SlateSerialisationUtil';
import HistorySideDrawer from '../HistorySideDrawer/HistorySideDrawer';
import { useDocumentHistory } from '../HistorySideDrawer/useDocumentHistory';

import styles from './DocumentContainer.module.scss';

type TemplateMetadata = Parameters<typeof initializeTemplateWithMetadata>[1];

interface DocumentContainerProps {
    // Redux-sourced data
    orgId: string;
    source: string;
    loading: boolean;
    companyName: string;

    // Redux action callbacks
    onUnmount: () => void;
    setLoading: (loading: boolean) => void;
    onSaveComplete: () => void;

    // API
    fetchTemplate: (params: { orgId: string; accountType: string; pnoId: string; primaryEmail: string }) => Promise<any>;

    // Save config
    docType: 'NDA' | 'ENGAGEMENT_LETTER' | 'ONE_PAGER' | 'BUREAU_KYC_CONSENT' | 'APPLICATION_FORM';

    // Optional: fetch extra template metadata (e.g. entityType for NDA)
    fetchExtraMetadata?: (orgId: string) => Promise<Partial<TemplateMetadata>>;

    // Regen modal
    regenTitle: string;

    // DocumentEditor config
    editorType: string;
    documentType: 'nda' | 'engagementLetter' | 'onePager' | 'bureauKycConsent' | 'applicationForm';
    fileNamePrefix: string;
    commentContainerId: string;
    mixpanelPreviewEvent: string;
    mixpanelSaveEvent: string;
    mixpanelRestoreVersionEvent?: string;
    enableHistory?: boolean;
}

function DocumentContainer({
    orgId,
    source,
    loading,
    companyName,
    onUnmount,
    setLoading,
    onSaveComplete,
    fetchTemplate,
    docType,
    fetchExtraMetadata,
    regenTitle,
    editorType,
    documentType,
    fileNamePrefix,
    commentContainerId,
    mixpanelPreviewEvent,
    mixpanelSaveEvent,
    mixpanelRestoreVersionEvent,
    enableHistory = false,
}: DocumentContainerProps) {
    const dispatch = useAppDispatch();
    const [savingData, setSavingData] = useState(false);
    const [userInfo, setUserInfo] = useState({});
    const [editorInitialData, setEditorInitialData] = useState<any>(null);
    const [editorReady, setEditorReady] = useState(false);
    const [showRegenConfirmModal, setShowRegenConfirmModal] = useState(false);

    const editorContentRef = useRef<Descendant[]>([]);

    const companyMainImg = useAppSelector((state) => state.user.domainMetadata?.mainImageUrl);
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const emailId = useAppSelector((state) => state.user.userData.primaryEmail);
    const user = useAppSelector((state) => state.user.userData);

    const historyContainerId = useMemo(() => `${commentContainerId}-history`, [commentContainerId]);

    useEffect(() => {
        return () => {
            onUnmount();
        };
    }, []);

    useEffect(() => {
        fetchTemplateData();
    }, [orgId]);

    useEffect(() => {
        if (editorInitialData) {
            setEditorReady(true);
        }
    }, [editorInitialData]);

    const fetchDataForDoc = async (isRegenerate = false) => {
        if (!isRegenerate) {
            setEditorReady(false);
        }
        setLoading(true);
        dispatch(
            fetchCompanyOverviewData({
                assesseeOrgId: orgId,
                investorOrgId: investorOrgId as string,
                onSuccess: async (data: OverviewData) => {
                    const [template, extraMeta] = await Promise.all([
                        fetchTemplate({
                            orgId,
                            accountType: 'INVESTOR',
                            pnoId: investorOrgId as string,
                            primaryEmail: emailId as string,
                        }),
                        fetchExtraMetadata ? fetchExtraMetadata(orgId) : Promise.resolve({}),
                    ]);
                    setEditorInitialData(
                        sanitizeSlateValue(
                            initializeTemplateWithMetadata(template || [], {
                                pnoLogo: companyMainImg as string,
                                companyName: companyName ?? '',
                                companyAddress: data?.headquarterLocation?.fullAddress ?? '',
                                userName: '',
                                userDesignation: '',
                                userEmail: '',
                                currentDate: dayjs().format(DATE_FORMAT),
                                ...extraMeta,
                            }),
                        ),
                    );
                    setEditorReady(true);
                    setLoading(false);
                },
                onError: () => {
                    setEditorReady(true);
                    setLoading(false);
                },
            }),
        );
    };

    const fetchTemplateData = () => {
        let timeout: NodeJS.Timeout;
        setEditorReady(false);
        setLoading(true);
        getOnePagerTemplateData({
            assesseeOrgId: orgId,
            investorOrgId,
            docType,
        })
            .then(({ onePagerData, templateData }) => {
                if (templateData?.length) {
                    setEditorInitialData(sanitizeSlateValue(templateData));
                    setUserInfo(onePagerData);
                    timeout = setTimeout(() => {
                        setEditorReady(true);
                        setLoading(false);
                    }, 200);
                } else {
                    fetchDataForDoc();
                }
            })
            .catch(() => {});
        return () => clearTimeout(timeout);
    };

    // Shared history-drawer integration (auto-save before open, version load/preview/restore,
    // editor remount via key bump, lastSaved snapshot). Active only when enableHistory=true.
    const history = useDocumentHistory({
        docType,
        investorOrgId: investorOrgId as string,
        assesseeOrgId: orgId,
        editorContentRef,
        enabled: enableHistory,
        onLoadVersion: setEditorInitialData,
        persistVersion: async (content, opts) => {
            await saveOnePagerTemplateData({
                assesseeOrgId: orgId,
                investorOrgId,
                onePagerData: content,
                email: emailId as string,
                docType,
                isRestored: opts?.isRestored,
            });
        },
    });

    const saveDoc = (editorContent, opts?: { isRestored?: boolean; skipClose?: boolean }) => {
        if (savingData) return;
        const isRestored = !!opts?.isRestored;
        const skipClose = !!opts?.skipClose;
        setSavingData(true);
        saveOnePagerTemplateData({
            assesseeOrgId: orgId,
            investorOrgId,
            onePagerData: editorContent || [],
            email: emailId as string,
            docType,
            isRestored,
        })
            .then(() => {
                history.markAsSaved(editorContent || []);
                setTimeout(() => {
                    if (!skipClose && !isRestored) {
                        onSaveComplete();
                    }
                    setSavingData(false);
                }, 1000);
            })
            .catch(() => {
                setSavingData(false);
            });
    };

    const handleRestoreConfirmTracking = useCallback(() => {
        if (mixpanelRestoreVersionEvent) {
            Mixpanel.track(mixpanelRestoreVersionEvent, {
                userRole: user?.accountType,
                pnoPlatform: getSubdomain(),
                userId: user?.userId,
                currentUrl: window.location.href,
                organizationId: orgId,
            });
        }
    }, [mixpanelRestoreVersionEvent, user, orgId]);

    return (
        <div>
            {editorReady && (
                <DocumentEditor
                    source={source}
                    generating={loading}
                    openModal={editorReady}
                    orgId={orgId}
                    orgName={companyName}
                    editorInitialData={editorInitialData}
                    editorReady={editorReady}
                    savingData={savingData}
                    saveDoc={saveDoc}
                    setShowRegenConfirmModal={setShowRegenConfirmModal}
                    userInfo={userInfo}
                    editorType={editorType}
                    documentType={documentType}
                    docType={docType}
                    fileNamePrefix={fileNamePrefix}
                    commentContainerId={commentContainerId}
                    historyContainerId={enableHistory ? historyContainerId : undefined}
                    mixpanelPreviewEvent={mixpanelPreviewEvent}
                    mixpanelSaveEvent={mixpanelSaveEvent}
                    mixpanelRestoreVersionEvent={mixpanelRestoreVersionEvent}
                    onToggleHistory={enableHistory ? history.handleToggleHistory : undefined}
                    historyOpen={enableHistory ? history.isOpen : undefined}
                    readOnly={enableHistory ? history.isOpen : undefined}
                    editorKey={enableHistory ? history.editorKey : undefined}
                    editorContentRef={enableHistory ? editorContentRef : undefined}
                />
            )}

            {enableHistory && (
                <HistorySideDrawer
                    containerId={historyContainerId}
                    isOpen={history.isOpen}
                    isExpanded={history.isExpanded}
                    title="Version History"
                    loading={history.loading}
                    previewLoading={history.previewLoading}
                    historyEntries={history.historyEntries}
                    onClose={history.closeDrawer}
                    onToggleExpand={history.toggleExpand}
                    onSelectEntry={history.selectHistoryEntry}
                    onPreviewEntry={history.previewHistoryEntry}
                    selectedEntryId={history.selectedHistoryEntryId}
                    onRestoreConfirm={handleRestoreConfirmTracking}
                />
            )}

            {showRegenConfirmModal && (
                <Modal open={showRegenConfirmModal} onClose={() => setShowRegenConfirmModal(false)} size="flexible">
                    <div className={styles.ConfirmationModal}>
                        <div className={styles.Title}>Regenerate {regenTitle}</div>
                        <div className={styles.Subtext}>
                            Are you sure you want to regenerate {regenTitle}? You will lose all the changes made so far.
                        </div>
                        <div className={styles.Buttons}>
                            <Button
                                text={'Cancel'}
                                onClick={() => setShowRegenConfirmModal(false)}
                                variant={'purpleinverted'}
                                style={{ marginTop: '0' }}
                            />
                            <Button
                                text={'Proceed Anyway'}
                                onClick={() => {
                                    setShowRegenConfirmModal(false);
                                    fetchDataForDoc(true);
                                }}
                                style={{ marginTop: '0' }}
                                variant={'primary'}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

export default DocumentContainer;
